/** Breadcrumb navigation for current storage folder path */

import { ChevronRight, HardDrive } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { StorageFolderNode } from '../../hooks/use-storage'

interface StorageBreadcrumbProps {
  folders: StorageFolderNode[]
  activeFolderId: string | null
  onSelect: (id: string | null) => void
  isDark: boolean
}

/** Walk the flat folder map upward to build the breadcrumb path */
function buildPath(
  folders: StorageFolderNode[],
  activeFolderId: string | null,
): StorageFolderNode[] {
  if (!activeFolderId) return []

  // Build a flat id→folder map from the tree
  const map = new Map<string, StorageFolderNode>()
  function flatten(nodes: StorageFolderNode[]) {
    for (const n of nodes) {
      map.set(n.id, n)
      flatten(n.children)
    }
  }
  flatten(folders)

  const path: StorageFolderNode[] = []
  let current: StorageFolderNode | undefined = map.get(activeFolderId)
  while (current) {
    path.unshift(current)
    current = current.parentId ? map.get(current.parentId) : undefined
  }
  return path
}

export function StorageBreadcrumb({ folders, activeFolderId, onSelect, isDark }: StorageBreadcrumbProps) {
  const path = buildPath(folders, activeFolderId)

  return (
    <nav className="flex items-center gap-1 text-xs" aria-label="Storage path">
      {/* Root segment */}
      <button
        onClick={() => onSelect(null)}
        className={cn(
          'flex items-center gap-1 rounded px-1 py-0.5',
          activeFolderId === null
            ? isDark ? 'font-semibold text-neutral-100' : 'font-semibold text-neutral-900'
            : isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700',
        )}
      >
        <HardDrive className="h-3 w-3" />
        Storage
      </button>

      {path.map((segment, i) => {
        const isLast = i === path.length - 1
        return (
          <span key={segment.id} className="flex items-center gap-1">
            <ChevronRight className={cn('h-3 w-3', isDark ? 'text-neutral-600' : 'text-neutral-400')} />
            {isLast ? (
              <span className={cn('font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
                {segment.name}
              </span>
            ) : (
              <button
                onClick={() => onSelect(segment.id)}
                className={cn(isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700')}
              >
                {segment.name}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}
